import { PointCloudNode, PointCloud } from '.';
import LocalScene from '../projections';
import { PythonExecutable } from '../../../executable';
import { LineSegments, Points, Vector3 } from 'three';
import { toast } from '../../../app';
import { t } from '../../../localization';
import { readFileBuffer } from '../file-system-loader';
import { fse, tempDirectoryPath } from '../../../electron-modules';
import { WorkerPool } from '../worker';

/** Processed pointcloud node */
class RawPointCloudNode extends PointCloudNode {
  public cloud: RawPointCloud;
  public parent: RawPointCloudRootNode;
  private path = null;
  private precision;
  private _shift;

  constructor(cloud, parent) {
    super();

    this.cloud = cloud;
    this.parent = parent;
  }

  get shift() {
    return this._shift;
  }

  set shift(value) {
    this._shift = value;
  }

  get workerPool() {
    return this.cloud.workerPool;
  }


  loadFromData(data) {
    this.numPoints = data.num_points;
    this.path = data.bytes_path;
    this.precision = data.precision;

    // Convert array to vector
    data.xyz_min = new Vector3(...data.xyz_min);
    data.xyz_max = new Vector3(...data.xyz_max);
    data.offset = new Vector3(...data.offset);

    // Calculate bounding box size
    let size = new Vector3()
      .add(data.xyz_max)
      .sub(data.xyz_min);

    // Calculate bounding box center
    let center = new Vector3()
      .add(data.xyz_min)
      .add(data.xyz_max)
      .divideScalar(2.0)
      .sub(data.offset);

    this.size = Math.max(size.x, size.y, size.z);
    this.nodeCenter = center;

    // Safe to add to parent's child list now
    this.parent.addChildNode(this);

    // Update root node info
    this.shift = LocalScene.offsetToScene(data.offset);

    this.cloud.updateColorLimits(data.rgb_max);
    this.cloud.updateIntensityRange(
      data.intensity_min, data.intensity_max);
    this.cloud.updateBoundingBox({
      min: data.xyz_min,
      max: data.xyz_max
    });
    this.cloud.updateRootNode();
  }

  getGeometry(data) {
    this.setGeometry(data);

    // Update unique values and min/max values
    this.cloud.updateClassifications(data.classification);

    this.loaded = true;
    this.loading = false;
  }

  addToScene() {
    // Create the pointcloud
    let pointsScene = new Points(this.geometry, this.cloud.materialScene);
    let pointsPicker = new Points(this.geometry, this.cloud.materialPicker);

    // Create bounding box object;
    let boundingBox = this.cloud.generateEdgeMesh(this.size);

    this.addCloudData(pointsPicker, pointsScene, boundingBox);
  }

  async getBinaryData() {
    const buffer = await readFileBuffer(this.path);
    fse.remove(this.path);

    this.sendToWorker({
      buffer: buffer,
      precision: this.precision,
      numPoints: this.numPoints
    });
  }

  sendToWorker(data) {
    let worker = this.workerPool.getWorker();

    worker.onmessage = event => {
      const data = event.data;

      // Convert raw buffer to float array
      data.positions = new Float32Array(data.positions);
      data.intensity = new Float32Array(data.intensity);
      data.classification = new Float32Array(data.classification);
      data.colors = new Float32Array(data.colors);

      this.getGeometry(data);

      this.workerPool.returnWorker(worker);
    };

    this.workerPool.sendMessage(worker, data, [data.buffer]);
  }

  async load() {
    if ((this.loaded) || (this.loading)) return;

    this.loading = true;

    this.getBinaryData();
  }
}

/** Simulated processed pointcloud root node */
class RawPointCloudRootNode extends RawPointCloudNode {
  constructor(cloud) {
    super(cloud, null);

    this.numPoints = 0;
    this.boundingBoxMesh = new LineSegments();
    this.nodeCenter = new Vector3();
    this.size = 0;
  }

  get center() {
    return this.nodeCenter;
  }

  /** Function override */
  dispose() {
    this.pointsScene = null;
    this.pointsPicker = null;
    this.boundingBoxMesh = null;
  }

  updateSizeInfo(data) {
    // Calculate bounding box size
    let size = new Vector3()
      .add(data.max)
      .sub(data.min);

    this.size = Math.max(size.x, size.y, size.z);

    // Calculate bounding box center
    let center = new Vector3()
      .add(data.min)
      .add(data.max)
      .divideScalar(2.0);

    this.nodeCenter = center;

    // Reset bounding geometry
    this._bounding_sphere = null;
    this._bounding_box = null;
  }

  async load() {
    if ((this.loaded) || (this.loading)) return;

    this.loaded = true;
    this.loading = false;
  }

  getGeometry() {
    return;
  }

  addToScene() {
    this.pointsScene = new Points();
    this.pointsPicker = new Points();
  }
}

/** Processed pointcloud */
export class RawPointCloud extends PointCloud {
  private workerExe: PythonExecutable;
  public workerPool: WorkerPool;

  private file = null;
  private header = null;
  public loaded = false;
  private loading: boolean;
  private cancelled = false;
  private loadStart;
  private timeEnd;
  private byteCutoffSize = 10 * 1024**3;
  private pointsDataParsed = 0;
  private numSampledPoints = 5000000;
  private minPointSize = 100000;
  public exeRunning = false;
  public canReadData = false;
  public percent;

  constructor(pointclouds) {
    super(pointclouds);

    this.workerExe = new PythonExecutable();

    const workerURL = "workers/point-decoder-buffer.js";
    const workerPath = `${process.env.PUBLIC_URL}/${workerURL}`;
    this.workerPool = new WorkerPool(workerPath);
  }

  get shift() {
    return LocalScene.sceneOffset;
  }

  get size() {
    return Math.max.apply(null, this.header.size);
  }

  /** Get bounding box with global shift applied */
  get tightBoundingBox() {
    let bbox = this._tightBoundingBox;
    if (!bbox) return null;

    let boundingBox = {
      min: new Vector3().add(bbox.min).sub(this.shift),
      max: new Vector3().add(bbox.max).sub(this.shift)
    };

    return boundingBox;
  }

  /** Get estimated bounding box using header information */
  get estimatedBoundingBox() {
    return {
      max: new Vector3(...this.header.maxs).sub(this.shift),
      min: new Vector3(...this.header.mins).sub(this.shift)
    };
  }

  get headerFileInfo() {
    return this.header?.file_info;
  }

  get fromJSON() {
    return this.file.fromJSON;
  }

  get isLargeFile() {
    let isLargeFile = this.header
      ? this.header.number_of_bytes > this.byteCutoffSize
      : false;

    return isLargeFile;
  }

  /** @private */
  destroyWorkers() {
    this.cancelled = true;
    this.workerExe.destroy();
    this.workerPool.terminateWorkers();
  }

  fileLoadStart() {
    this.loading = true;
    this.loaded = false;
    this.updatePercent(0);
  }

  fileLoadFinish() {
    this.loading = false;
    this.loaded = true;
    this.timeEnd = performance.now();
    this.updatePercent(100);
    const elapsed = (this.timeEnd - this.loadStart) / 1000;
    console.log(`Point cloud loaded: ${this.file.name}. ${elapsed.toFixed(2)}s`);
  }

  fileLoadCancel() {
    this.loading = false;
    this.loaded = false;
  }

  fileLoadProgress() {
    let points_to_process = this.header.points_to_process;
    let percent = this.pointsDataParsed / points_to_process;
    this.updatePercent(100 * percent);
  }

  updatePercent(percent) {
    this.percent = percent;
  }

  updateRootNode() {
    this.root.updateSizeInfo(this.tightBoundingBox);
  }

  load(file) {
    this.file = file;

    this.pointsDataParsed = 0;
    this.canReadData = true;

    // Add pointcloud and set parameters
    this.pointclouds.addPointCloud(this);
    this.setIdentifier(file.identifier);
    this.setVisibility(file.visible);

    this.fileLoadStart();
  }

  fileLoadFinished(success) {
    if (success) {
      this.fileLoadFinish();
    } else {
      this.fileLoadCancel();
    }
  }

  launchExecutable() {
    this.exeRunning = true;
    this.loadStart = performance.now();

    const lasFilePath = this.file.path;

    let commands = [
      "-p", "convert_engine_points",
      "--input_file", lasFilePath,
      "--output_folder", tempDirectoryPath,
      "--units", LocalScene.dataProjectionUnits,
      "--min_chunk_size", this.minPointSize,
      "--max_points", this.numSampledPoints
    ];

    this.workerExe.run({
      command: commands,
      onLine: jsonData => {
        if (!jsonData) return;
        const {type, data} = jsonData;

        if (type === "header") {
          this.parseHeaderResponse(data);
        } else if (type === "points") {
          this.parsePointsResponse(data);
        }
      },
      onClose: () => {
        if (this.cancelled) return;

        const pointsToProcess = this.header?.points_to_process;
        const allPointsLoaded = pointsToProcess === this.pointsDataParsed;

        if (!allPointsLoaded) {
          this.error = true;
          toast.error(t("toast.points-load-error", {
            name: this.file.name
          }));
        }

        this.fileLoadFinished(true);
      }
    });
  }

  parseHeaderResponse(data) {
    this.header = data;
    this.root = new RawPointCloudRootNode(this);

    if (this.isLargeFile) {;
      toast.warning(t("toast.large-point-cloud", {
        name: this.file.name
      }));
    }
  }

  parsePointsResponse(data) {
    // Update loaded percent
    this.pointsDataParsed += data['point_count'];
    this.fileLoadProgress();

    // Add new node to pointcloud
    let child = new RawPointCloudNode(this, this.root);
    child.loadFromData(data);

    this.pointclouds.updateMinimapExtent();

    if (this.fromJSON) return;
    this.viewer.zoomToSceneExtent(this.estimatedBoundingBox, false);
  }

  getVisibleNodes(condition?): PointCloudNode[] {
    let visibleNodes = [];

    // All data for raw pointclouds is contained in a single level
    this.root?.children.forEach(node => {
      if (!node.visible) return;
      if (condition && !condition(node)) return;

      visibleNodes.push(node);
    });

    return visibleNodes;
  }

  hideDescendants() {
    if (!this.root) return;

    [this.root, ...this.root.children].forEach(node => {
      node.setVisibility(false, false);
    });
  }

  updateVisibility(frustum, boundsVisible) {
    if (!this.visible || !this.root) return 0;

    let unloadedGeometry = [];
    let numLoadedPoints = 0;
    let loadedToGPUThisFrame = 0;

    [this.root, ...this.root.children].forEach(node => {
      // Ignore nodes based on our view frustrum
      if (!frustum.intersectsBox(node.boundingBox)) {
        return;
      }

      // Add node to scene if data is loaded, or else add to unloaded list
      if (node.isGeometryNode && (!node.parent || node.parent.isTreeNode)) {
        if (node.loaded && loadedToGPUThisFrame < this.maxloadedToGPUPerFrame) {
          node.addToScene();
          loadedToGPUThisFrame++;
        } else {
          unloadedGeometry.push(node);
        }
      }

      // Update node visibility for loaded nodes
      if (node.isTreeNode) {
        node.setVisibility(true, boundsVisible);
        numLoadedPoints += node.numPoints;
      }
    });

    unloadedGeometry = unloadedGeometry.slice(0, this.maxUnloadedNodes);
    unloadedGeometry.forEach(node => {
      node.load();
    });

    return numLoadedPoints;
  }
}
